/**
 * Client-side helpers for the test-inbox debug-send endpoints. Inlined
 * rather than depending on `@enduring-systems/test-inbox-client` because
 * the published SDK predates these routes. Mirrors the pattern in
 * `messages-api.ts` and `namespaces-api.ts`.
 */

export interface NamespaceAddress {
  id: string;
  namespaceId: string;
  email: string;
}

export interface DebugSendBody {
  addressLabel?: string;
  subject?: string;
  htmlBody?: string;
  textBody?: string;
}

export type SendDebugMessageResult =
  | { status: 200; messageId: string }
  | { status: number; messageId?: undefined; errorMessage?: string };

export async function fetchNamespaceAddresses(
  baseUrl: string,
  namespaceId: string
): Promise<NamespaceAddress[]> {
  const res = await fetch(`${baseUrl}/namespaces/${namespaceId}/addresses`);
  if (!res.ok) {
    throw new Error(`fetchNamespaceAddresses failed with status ${res.status}`);
  }
  const data = (await res.json()) as { items: NamespaceAddress[] };
  return data.items;
}

export async function sendDebugMessage(
  baseUrl: string,
  namespaceId: string,
  body: DebugSendBody
): Promise<SendDebugMessageResult> {
  const res = await fetch(`${baseUrl}/namespaces/${namespaceId}/debug-send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (res.ok) {
    const data = (await res.json()) as { messageId: string };
    return { status: 200, messageId: data.messageId };
  }

  let errorMessage: string | undefined;
  try {
    const errData = (await res.json()) as { message?: string };
    if (typeof errData.message === "string") {
      errorMessage = errData.message;
    }
  } catch {
    // body may not be JSON
  }
  return errorMessage !== undefined ? { status: res.status, errorMessage } : { status: res.status };
}
